import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight, Heart } from "lucide-react";
import { useLang } from "@/i18n";
import { IMG } from "@/data/media";
import { MaskLines } from "@/components/motion";
import { L } from "@/components/L";

const RotatingBadge = ({ text }) => {
  const chars = `${text.toUpperCase()} · `.split("");
  return (
    <div className="relative w-full h-full spin-slow" aria-hidden="true">
      {chars.map((c, i) => (
        <span key={i} className="absolute left-1/2 top-0 h-1/2 origin-bottom text-[10px] sm:text-[11px] font-bold text-[var(--ink)]" style={{ transform: `translateX(-50%) rotate(${(360 / chars.length) * i}deg)` }}>{c}</span>
      ))}
    </div>
  );
};

const Flower = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 70]);
  const petalY = useTransform(scrollY, [0, 700], [0, -50]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px]" data-testid="hero-flower">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="grid grid-cols-2 gap-3 sm:gap-4">
        <motion.div style={{ y: petalY }} initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.5, ease: [0.2, 0.8, 0.2, 1] }} className="aspect-square petal-br bg-[var(--blue)] origin-bottom-right" />
        <motion.div initial={{ clipPath: "circle(0% at 0% 100%)" }} animate={{ clipPath: "circle(145% at 0% 100%)" }} transition={{ duration: 1.5, delay: 0.25, ease: [0.7, 0, 0.2, 1] }} className="aspect-square petal-bl overflow-hidden relative bg-[var(--green-pale)] [transform:translateZ(40px)]">
          <motion.img style={{ y: imgY, scale: 1.22 }} src={IMG.handsWater} alt={t.gallery.captions.handsWater} className="photo absolute inset-0 w-full h-full object-cover" fetchpriority="high" />
        </motion.div>
        <motion.div initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, delay: 0.7, ease: [0.2, 0.8, 0.2, 1] }} className="aspect-square petal-tr bg-[var(--coral)] origin-top-right" />
        <motion.div initial={{ opacity: 0, rotate: -40 }} animate={{ opacity: 1, rotate: 0 }} transition={{ duration: 1.2, delay: 0.9 }} className="aspect-square relative grid place-items-center">
          <div className="absolute inset-[6%] petal-tl bg-[var(--yellow)]" />
          <div className="absolute inset-[12%]"><RotatingBadge text="Fundación MADE · MADE ONLUS" /></div>
          <img src={IMG.logo} alt="" className="relative w-[26%] h-auto" />
        </motion.div>
      </motion.div>
    </div>
  );
};

export const HomeHero = () => {
  const { t } = useLang();
  const h = t.home;
  return (
    <section className="relative min-h-[100svh] pt-28 sm:pt-32 pb-16 overflow-hidden" data-testid="home-hero">
      <div className="wrap grid gap-14 lg:grid-cols-12 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.span className="eyebrow" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>{h.eyebrow}</motion.span>
          <MaskLines
            lines={h.titleLines}
            testId="hero-title"
            className="mt-7 text-[2.75rem] leading-[1] sm:text-7xl lg:text-[4.6rem] xl:text-[5.5rem] tracking-[-0.02em] text-[var(--ink)]"
            lastClassName="italic text-[var(--green-deep)]"
            delay={0.15}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.9 }} className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--ink-soft)]" data-testid="hero-subtitle">
            {h.subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }} className="mt-10 flex flex-wrap gap-3">
            <L to="about" className="btn btn-primary group" data-testid="hero-cta-mission">{h.ctaMission} <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" /></L>
            <L to="donate" className="btn btn-yellow" data-testid="hero-cta-support"><Heart size={16} /> {h.ctaSupport}</L>
          </motion.div>
        </div>
        <div className="lg:col-span-5 w-full max-w-[460px] mx-auto lg:max-w-none">
          <Flower />
        </div>
      </div>
      <motion.a href="#home-about" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="hidden lg:flex absolute bottom-8 left-1/2 -translate-x-1/2 items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--ink-soft)]" data-testid="hero-scroll-cue">
        <ArrowDown size={14} className="animate-bounce" /> {h.scroll}
      </motion.a>
    </section>
  );
};
